// upload.ts — attributed session upload to R2 (EGO-98).
//
// The ownership ledger made real: a pulled session is uploaded under the
// signed-in user's prefix (captures/{user_id}/{session_id}/) through Worker
// presigned PUTs — no R2 keys in the client. manifest.json (file inventory +
// sha256s + raw/derived tags) is generated here at upload time and uploaded
// LAST as the atomic commit marker; /upload/complete then flips the sessions
// row to `complete`. meta.json stays the capture-time semantic descriptor —
// two documents, two jobs.
//
// Provenance rule: the session's stamped meta.user_id MUST match the signed-in
// user — mismatches (and unstamped pre-binding sessions) refuse to upload.
import { createSHA256 } from "hash-wasm";
import { supabase } from "./auth.ts";
import { WORKER_URL } from "./apk.ts";
import { breadcrumb } from "./errmon.ts";

export const MANIFEST_SCHEMA = 1;

// Derived artifacts (Portal/viewer products) live under derived/ in R2 so the
// raw capture stays byte-immutable. Everything else is raw. Explicit names, not
// extensions: since schema v5 left.mp4/right.mp4 ARE the raw capture (EGO-130),
// so only the review proxy is a derived mp4. Mirrored in tools/admin_offload.py.
const DERIVED_RE = /^(transcript\.json|task_transcript\.json|transcript_edits\.json|qa_certificate\.json|proxy\.mp4|.+\.ply)$/;

export interface UploadProgress {
  phase: "hash" | "upload" | "commit" | "done";
  file?: string;
  bytesDone: number;
  bytesTotal: number;
}

// ── big-file strategy (EGO-86) ───────────────────────────────────────────────
// R2 caps a single presigned PUT at 5 GB — a 45-min 60 fps eye is ~20 GB, so
// big files go MULTIPART through the worker's R2 binding (/upload/mpu-*):
// 64 MB parts, 3 in flight, per-part retry with backoff. Measured on Rob's
// uplink (2026-08-05): 3 concurrent streams ≈ 9.4 MB/s vs 7.7 single — and a
// network blip costs one part, not the whole file (this network provably
// flakes mid-stream). Small files keep presigned PUTs but gain the same
// retry + a shared 3-slot pool.
const MPU_THRESHOLD = 256 * 1048576;   // files bigger than this go multipart
const PART_BYTES = 64 * 1048576;       // safely under the Workers body cap
const CONCURRENCY = 3;                 // saturates the measured uplink
const TRIES = 5;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const backoff = (attempt: number) => sleep(Math.min(15000, 1000 * 2 ** attempt) * (0.5 + Math.random()));

/** Throw an Error tagged with a phase + structured details. errmon's
 *  reportError merges `.info` into the Bugsnag event, so every distinct
 *  failure mode arrives as its own context (upload:presign, upload:mpu-part…)
 *  with the HTTP status / part number / file attached — not just a message. */
function fail(phase: string, message: string, extra?: Record<string, unknown>): never {
  const e = new Error(message) as Error & { info: Record<string, unknown> };
  e.info = { phase, ...extra };
  throw e;
}

/** PUT a blob with progress + retries. Resolves with the ETag header (may be null). */
function putBlob(url: string, blob: Blob, onLoaded: (n: number) => void): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    let last = 0;
    xhr.upload.onprogress = (e) => { onLoaded(e.loaded - last); last = e.loaded; };
    // R2/worker error bodies are short XML/JSON — the first line is the story.
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300
      ? resolve(xhr.getResponseHeader("ETag"))
      : reject(new Error(`PUT ${xhr.status}: ${(xhr.responseText || "").slice(0, 200)}`));
    xhr.onerror = () => reject(new Error("PUT network error (connection dropped or blocked)"));
    xhr.send(blob);
  });
}

async function putWithRetry(url: string, blob: Blob, onLoaded: (n: number) => void, label: string): Promise<string | null> {
  let sent = 0;
  const track = (n: number) => { sent += n; onLoaded(n); };
  for (let attempt = 0; ; attempt++) {
    try { return await putBlob(url, blob, track); }
    catch (e) {
      onLoaded(-sent); sent = 0;            // roll the progress bar back honestly
      breadcrumb(`upload retry: ${label}`, { attempt: attempt + 1, error: (e as Error).message, bytes: blob.size });
      if (attempt + 1 >= TRIES) fail("put", `${label}: ${(e as Error).message} (${TRIES} attempts)`,
        { file: label, attempts: TRIES, blobBytes: blob.size, lastError: (e as Error).message });
      await backoff(attempt);
    }
  }
}

/** Run jobs with a fixed number of workers; first failure wins, others drain. */
async function pool<T>(jobs: (() => Promise<T>)[], width: number): Promise<T[]> {
  const out: T[] = new Array(jobs.length);
  let next = 0;
  let failed: unknown = null;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= jobs.length || failed) return;
      try { out[i] = await jobs[i](); }
      catch (e) { failed = failed ?? e; return; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(width, jobs.length) }, worker));
  if (failed) throw failed;
  return out;
}

/** Multipart upload of one big file through the worker's R2 binding. */
async function uploadMultipart(
  f: LocalFile, path: string, ids: { session_id: string },
  onLoaded: (n: number) => void, log: (m: string) => void,
): Promise<void> {
  const headers = await authHeaders();
  const cr = await fetch(`${WORKER_URL}/upload/mpu-create`, {
    method: "POST", headers, body: JSON.stringify({ session_id: ids.session_id, path }),
  }).catch((e) => fail("mpu-create", `mpu-create ${path}: ${e?.message ?? e}`, { file: path, network: true }));
  if (!cr.ok) fail("mpu-create", `mpu-create ${path}: ${cr.status} ${await cr.text()}`,
    { file: path, status: cr.status });
  const t = await cr.json();   // { key, upload_id, exp, ticket }
  const q = `key=${encodeURIComponent(t.key)}&upload_id=${encodeURIComponent(t.upload_id)}&exp=${t.exp}&ticket=${t.ticket}`;

  const nParts = Math.ceil(f.file.size / PART_BYTES);
  log(`  ${path}: multipart, ${nParts} × 64 MB parts, ${CONCURRENCY} in flight`);
  const parts: { part: number; etag: string }[] = new Array(nParts);
  let partsDone = 0;
  try {
    await pool(Array.from({ length: nParts }, (_, i) => async () => {
      const blob = f.file.slice(i * PART_BYTES, Math.min((i + 1) * PART_BYTES, f.file.size));
      const res = await putWithRetryJson(`${WORKER_URL}/upload/mpu-part?${q}&n=${i + 1}`, blob, onLoaded, `${path} part ${i + 1}`);
      parts[i] = { part: i + 1, etag: res.etag };
      partsDone++;
    }), CONCURRENCY);
  } catch (e: any) {
    // Re-tag with how far the multipart got — "died on part 2 of 300" and
    // "died on part 299" are different diagnoses.
    e.info = { ...e.info, phase: "mpu-part", file: path, fileBytes: f.file.size, nParts, partsDone };
    await fetch(`${WORKER_URL}/upload/mpu-abort?${q}`, { method: "POST" }).catch(() => {});
    throw e;
  }
  breadcrumb(`multipart parts done: ${path}`, { nParts, fileBytes: f.file.size });
  const done = await fetch(`${WORKER_URL}/upload/mpu-complete?${q}`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ parts }),
  }).catch((e) => fail("mpu-complete", `mpu-complete ${path}: ${e?.message ?? e}`, { file: path, network: true, nParts }));
  if (!done.ok) fail("mpu-complete", `mpu-complete ${path}: ${done.status} ${await done.text()}`,
    { file: path, status: done.status, nParts });
}

/** Like putWithRetry but the endpoint answers JSON ({ etag }) — the mpu-part route. */
async function putWithRetryJson(url: string, blob: Blob, onLoaded: (n: number) => void, label: string): Promise<{ etag: string }> {
  let sent = 0;
  for (let attempt = 0; ; attempt++) {
    try {
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        let last = 0;
        xhr.upload.onprogress = (e) => { sent += e.loaded - last; onLoaded(e.loaded - last); last = e.loaded; };
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300)
            return reject(new Error(`${xhr.status}: ${(xhr.responseText || "").slice(0, 200)}`));
          try {
            const j = JSON.parse(xhr.responseText);
            j.etag ? resolve(j) : reject(new Error(`no etag: ${xhr.responseText.slice(0, 120)}`));
          } catch { reject(new Error("bad json from mpu-part")); }
        };
        xhr.onerror = () => reject(new Error("network error (connection dropped or blocked)"));
        xhr.send(blob);
      });
    } catch (e) {
      onLoaded(-sent); sent = 0;
      breadcrumb(`upload retry: ${label}`, { attempt: attempt + 1, error: (e as Error).message, bytes: blob.size });
      if (attempt + 1 >= TRIES) fail("put", `${label}: ${(e as Error).message} (${TRIES} attempts)`,
        { file: label, attempts: TRIES, blobBytes: blob.size, lastError: (e as Error).message });
      await backoff(attempt);
    }
  }
}

interface LocalFile { rel: string; file: File; kind: "raw" | "derived"; }

async function walk(dir: FileSystemDirectoryHandle, prefix = ""): Promise<LocalFile[]> {
  const out: LocalFile[] = [];
  for await (const [name, handle] of (dir as any).entries()) {
    if (handle.kind === "directory") {
      out.push(...await walk(handle, `${prefix}${name}/`));
    } else {
      const rel = `${prefix}${name}`;
      out.push({ rel, file: await handle.getFile(), kind: DERIVED_RE.test(rel) ? "derived" : "raw" });
    }
  }
  return out;
}

async function sha256Stream(file: File): Promise<string> {
  const hasher = await createSHA256();
  const reader = file.stream().getReader();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    hasher.update(value);
  }
  return hasher.digest("hex");
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("not signed in");
  return { Authorization: `Bearer ${session.access_token}`, "content-type": "application/json" };
}

/** status of already-uploaded sessions, keyed by session id (RLS: own rows only). */
export type UploadedRow = {
  status: string; uploadedAt: string | null;
  qaStatus: string | null; taskId: string | null; isTest: boolean;
  collectionId: string | null; capturedAt: string | null;
  reviewedAt: string | null;
};
export async function uploadedStatus(): Promise<Map<string, UploadedRow>> {
  const { data, error } = await supabase.from("sessions")
    .select("id, status, uploaded_at, qa_status, task_id, is_test, collection_id, captured_at, reviewed_at");
  if (error) return new Map();
  return new Map(data.map((r: any) => [r.id, {
    status: r.status, uploadedAt: r.uploaded_at,
    qaStatus: r.qa_status ?? null, taskId: r.task_id ?? null, isTest: !!r.is_test,
    collectionId: r.collection_id ?? null, capturedAt: r.captured_at ?? null,
    reviewedAt: r.reviewed_at ?? null,
  }]));
}

export async function uploadSession(
  outRoot: FileSystemDirectoryHandle, id: string,
  opts: { isTest?: boolean; onProgress?: (p: UploadProgress) => void; log?: (m: string) => void } = {},
): Promise<{ files: number; bytes: number; qaStatus: string | null }> {
  const log = opts.log ?? (() => {});
  if (id.includes("/")) fail("preflight", "collection uploads not supported yet — upload clips individually once EGO-98 grows collection support", { sessionId: id });

  const dir = await outRoot.getDirectoryHandle(id);
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) fail("preflight", "not signed in");

  // Provenance assert (EGO-98): only the recording contractor may upload.
  let meta: any;
  try { meta = JSON.parse(await (await (await dir.getFileHandle("meta.json")).getFile()).text()); }
  catch { fail("preflight", "no parseable meta.json — not a session directory", { sessionId: id }); }
  if (!meta.user_id) fail("preflight", "session has no identity stamp (recorded before device binding) — refusing to upload", { sessionId: id });
  if (meta.user_id !== user.id) fail("preflight", `provenance mismatch: session was recorded by ${meta.user_id}, you are ${user.id}`, { sessionId: id });

  // PII gate (EGO-163): a session that declares 3D privacy regions may only
  // upload once those regions have been burned into left/right.mp4 — mask_burn.json
  // is the marker maskburn.ts writes after re-encoding both eyes. Enforced HERE,
  // not just in the sync flows, so no future path can ship raw masked-session
  // video. Fail closed: an unreadable/absent marker refuses the whole upload.
  const maskCount = Number(meta?.masks?.count ?? 0);
  if (maskCount > 0) {
    let burned = false;
    try { await dir.getFileHandle("mask_burn.json"); burned = true; } catch { /* not burned */ }
    if (!burned) {
      fail("preflight", "session has PII masks but video is not masked — run Sync again",
        { sessionId: id, maskRegions: maskCount });
    }
  }

  const files = await walk(dir);
  if (!files.length) fail("preflight", "empty session directory", { sessionId: id });
  const bytesTotal = files.reduce((a, f) => a + f.file.size, 0);

  // 1. Inventory + checksums (streamed — sessions are multi-GB).
  let bytesDone = 0;
  const entries: { path: string; size: number; sha256: string; kind: string }[] = [];
  for (const f of files) {
    opts.onProgress?.({ phase: "hash", file: f.rel, bytesDone, bytesTotal });
    const path = (f.kind === "derived" ? "derived/" : "") + f.rel;
    try {
      entries.push({ path, size: f.file.size, sha256: await sha256Stream(f.file), kind: f.kind });
    } catch (e: any) {
      // Read failures here mean the staged copy is unreadable (OPFS eviction,
      // file moved/locked) — a different beast from any network failure.
      fail("hash", `reading ${f.rel}: ${e?.message ?? e}`, { file: f.rel, fileBytes: f.file.size, errName: e?.name });
    }
    bytesDone += f.file.size;
  }

  const manifest = {
    manifest_schema: MANIFEST_SCHEMA,
    session_id: id,
    user_id: meta.user_id,
    device_id: meta.device_id ?? null,
    captured_at: meta.start_wall_ms ? new Date(meta.start_wall_ms).toISOString() : null,
    is_test: !!opts.isTest,
    files: entries,
  };

  // Task attribution (EGO-132 A): task-capture sessions carry a task.json
  // snapshot whose id is the store uuid for store tasks. Sent as-is; the
  // worker validates against the tasks table and drops anything else (the
  // bundled fallback's slug id, pre-store sessions).
  let taskId: string | null = null;
  try {
    const t = JSON.parse(await (await (await dir.getFileHandle("task.json")).getFile()).text());
    if (typeof t.id === "string") taskId = t.id;
  } catch { /* free capture — no task */ }

  // 2. Presign the small files (creates/updates the sessions row →
  // `uploading`). Files past MPU_THRESHOLD skip presigning entirely — they go
  // multipart through the worker (R2 caps a single PUT at 5 GB).
  const big = files.filter((f) => f.file.size > MPU_THRESHOLD);
  const small = files.filter((f) => f.file.size <= MPU_THRESHOLD);
  const pathOf = (f: LocalFile) => (f.kind === "derived" ? "derived/" : "") + f.rel;
  const presignBody = {
    session_id: id,
    device_id: meta.device_id ?? null,
    captured_at: manifest.captured_at,
    is_test: !!opts.isTest,
    task_id: taskId,
    files: [...small.map((f) => ({ path: pathOf(f) })), { path: "manifest.json" }],
  };
  const pr = await fetch(`${WORKER_URL}/upload/presign`, {
    method: "POST", headers: await authHeaders(), body: JSON.stringify(presignBody),
  }).catch((e) => fail("presign", `presign: ${e?.message ?? e}`, { network: true }));
  if (!pr.ok) fail("presign", `presign failed: ${pr.status} ${await pr.text()}`, { status: pr.status });
  const { urls, r2_prefix } = await pr.json();
  log(`uploading ${files.length} files (${(bytesTotal / 1048576).toFixed(1)} MB) → ${r2_prefix}`);

  // 3. Upload the data files — big ones first (they're the long pole), then
  // the smalls, all through a shared retry + concurrency pool. XHR instead of
  // fetch: fetch has no upload-progress events. Emits throttled to ~10/s.
  bytesDone = 0;
  let lastEmit = 0;
  let currentFile = "";
  const onLoaded = (n: number) => {
    bytesDone += n;
    const now = Date.now();
    if (now - lastEmit < 100) return;
    lastEmit = now;
    opts.onProgress?.({ phase: "upload", file: currentFile, bytesDone, bytesTotal });
  };

  for (const f of big) {
    currentFile = pathOf(f);
    opts.onProgress?.({ phase: "upload", file: currentFile, bytesDone, bytesTotal });
    await uploadMultipart(f, currentFile, { session_id: id }, onLoaded, log);
  }

  // Re-presign after the big files: the URLs above were minted BEFORE a
  // multipart leg that can run for hours (23 GB at residential uplink), so by
  // the time the smalls + manifest need them they may be expired. A fresh
  // presign right before use means no URL ever races a long transfer.
  // (/upload/presign is idempotent on the sessions row.)
  let putUrls = urls;
  if (big.length) {
    const pr2 = await fetch(`${WORKER_URL}/upload/presign`, {
      method: "POST", headers: await authHeaders(), body: JSON.stringify(presignBody),
    }).catch((e) => fail("presign-refresh", `presign refresh: ${e?.message ?? e}`, { network: true }));
    if (!pr2.ok) fail("presign-refresh", `presign refresh failed: ${pr2.status} ${await pr2.text()}`, { status: pr2.status });
    putUrls = (await pr2.json()).urls;
    breadcrumb("presigned URLs refreshed after big files", { smallFiles: small.length });
  }
  await pool(small.map((f) => () => {
    const path = pathOf(f);
    currentFile = path;
    return putWithRetry(putUrls[path], f.file, onLoaded, path);
  }), CONCURRENCY);

  // 4. manifest.json LAST — the session isn't real in R2 until this lands.
  opts.onProgress?.({ phase: "commit", bytesDone, bytesTotal });
  const mr = await fetch(putUrls["manifest.json"], {
    method: "PUT", body: new Blob([JSON.stringify(manifest, null, 1)], { type: "application/json" }),
  }).catch((e) => fail("manifest", `manifest PUT: ${e?.message ?? e}`, { network: true }));
  if (!mr.ok) fail("manifest", `manifest PUT failed: ${mr.status} ${(await mr.text()).slice(0, 200)}`, { status: mr.status });

  // 5. Flip the ledger row to complete (+ QA verdict when the gate has run).
  let qaStatus: string | null = null;
  try {
    const cert = JSON.parse(await (await (await dir.getFileHandle("qa_certificate.json")).getFile()).text());
    if (typeof cert.verdict === "string") qaStatus = cert.verdict;
  } catch { /* no certificate — QA not run yet */ }
  const cr = await fetch(`${WORKER_URL}/upload/complete`, {
    method: "POST", headers: await authHeaders(),
    body: JSON.stringify({ session_id: id, ...(qaStatus ? { qa_status: qaStatus } : {}) }),
  }).catch((e) => fail("complete", `complete: ${e?.message ?? e}`, { network: true }));
  if (!cr.ok) fail("complete", `complete failed: ${cr.status} ${(await cr.text()).slice(0, 200)}`, { status: cr.status });

  opts.onProgress?.({ phase: "done", bytesDone, bytesTotal });
  return { files: files.length, bytes: bytesTotal, qaStatus };
}
